import React, { useState, useRef, useEffect } from "react";
import { authService } from "../../services";

interface OTPPageProps {
  email: string;
  onOTPVerified: (otp: string) => void;
  onBackToLogin: () => void;
}

const OTPPage: React.FC<OTPPageProps> = ({
  email,
  onOTPVerified,
  onBackToLogin,
}) => {
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [error, setError] = useState<string>("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Start countdown for resend
    if (resendCountdown > 0) {
      const timer = setTimeout(
        () => setResendCountdown(resendCountdown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      setError("Vui lòng nhập đầy đủ 6 số OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await authService.verifyPasswordResetOTP(email, otpString);
      console.log("OTP verification successful");
      onOTPVerified(otpString);
    } catch (error: any) {
      console.error("OTP verification failed:", error);
      setError(error.message || "Mã OTP không đúng. Vui lòng thử lại.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendCountdown(60);
    setError("");

    try {
      await authService.sendPasswordResetOTP(email);
      console.log("OTP resent successfully");
    } catch (error: any) {
      console.error("Resend OTP failed:", error);
      setError(error.message || "Gửi lại mã OTP thất bại. Vui lòng thử lại.");
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-xs">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-black mb-2">Xác thực OTP</h1>
          <p className="text-sm text-gray-600 mb-2">Mã OTP đã được gửi đến</p>
          <p className="text-sm font-medium text-black">{email}</p>
        </div>

        {/* OTP Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* OTP Input */}
          <div>
            <label className="block text-sm text-black mb-3">
              Nhập mã OTP 6 số
            </label>
            <div className="flex justify-between space-x-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-12 text-center border border-gray-400 rounded text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder=""
                />
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || otp.join("").length !== 6}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Đang xác thực..." : "Xác thực OTP"}
          </button>
        </form>

        {/* Resend OTP */}
        <div className="text-center mt-4">
          {resendCountdown > 0 ? (
            <p className="text-sm text-gray-500">
              Gửi lại mã sau {resendCountdown}s
            </p>
          ) : (
            <button
              type="button"
              onClick={handleResendOTP}
              className="text-blue-600 text-sm hover:underline"
            >
              Gửi lại mã OTP
            </button>
          )}
        </div>

        {/* Back to Login */}
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={onBackToLogin}
            className="text-blue-600 text-sm"
          >
            ← Quay lại đăng nhập
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPPage;
